import { FunctionsHttpError } from '@supabase/functions-js'

function stringifyErrorValue(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (value && typeof value === 'object') {
    const record = value as { message?: unknown; error?: unknown; details?: unknown }
    if (typeof record.message === 'string' && record.message.trim()) return record.message.trim()
    if (typeof record.error === 'string' && record.error.trim()) return record.error.trim()
    if (typeof record.details === 'string' && record.details.trim()) return record.details.trim()
    try {
      return JSON.stringify(value)
    } catch {
      return null
    }
  }
  return null
}

/**
 * Supabase `functions.invoke` returns a generic message for non-2xx responses.
 * The Edge Function JSON body (error, details, hint) is on the Response in `error.context`.
 */
export async function getSupabaseFunctionErrorMessage(
  err: unknown,
  fallback = 'Request failed',
): Promise<string> {
  if (err instanceof FunctionsHttpError && err.context) {
    try {
      const body = await err.context.clone().json()
      if (body && typeof body === 'object' && 'error' in body) {
        const message = stringifyErrorValue((body as { error?: unknown }).error)
        if (!message) return fallback
        const parts = [message]
        const details = (body as { details?: string }).details
        const hint = (body as { hint?: string }).hint
        if (typeof details === 'string' && details.trim()) parts.push(details.trim())
        if (typeof hint === 'string' && hint.trim()) parts.push(hint.trim())
        return parts.join(' — ')
      }
    } catch {
      /* use fallback */
    }
  }
  if (err instanceof Error && err.message) return err.message
  return stringifyErrorValue(err) ?? fallback
}
